import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import omnicareLogo from '../assets/icons/logo.svg';
import {
  CheckCircle2, ChevronDown, ChevronUp, Share2,
  ShieldCheck, BookOpen, ArrowRight, Activity,
  Heart, Download, FileText,
  Sparkles, Camera, Users, X, Twitter,
  Loader2, Image as ImageIcon
} from 'lucide-react';

// ─── Utility loaders ───
const loadHtml2Canvas = () => new Promise((resolve, reject) => {
  if (window.html2canvas) { resolve(window.html2canvas); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
  s.onload = () => resolve(window.html2canvas);
  s.onerror = () => reject(new Error('Failed to load html2canvas'));
  document.body.appendChild(s);
});
const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  s.onload = () => resolve(window.jspdf.jsPDF);
  s.onerror = () => reject(new Error('Failed to load jsPDF'));
  document.body.appendChild(s);
});

// ─── Cancer copy ───
const CANCER_COPY = {
  lung: { elevated: "A low-dose CT scan is quick, painless, and catches issues when they're most treatable.", routine: "No immediate screening needed.", action: { elevated: "Ask about a low-dose CT scan", routine: "Learn about lung screening" } },
  breast: { elevated: "Mammograms are one of the most effective screening tools.", routine: "Regular mammograms at the recommended age are genuinely lifesaving.", action: { elevated: "Schedule a mammogram", routine: "Check your mammogram timeline" } },
  cervical: { elevated: "A Pap test with HPV co-testing is the gold standard.", routine: "Stay on top of routine Pap + HPV tests.", action: { elevated: "Book your Pap + HPV test", routine: "Confirm you're up to date" } },
  colorectal: { elevated: "Multiple options — from at-home stool tests to colonoscopy.", routine: "Screening starts at age 45 for most people.", action: { elevated: "Discuss early screening", routine: "Plan your first screening" } },
  gastric: { elevated: "An upper endoscopy is the most common first step.", routine: "Bring up persistent digestive symptoms with your doctor.", action: { elevated: "Ask about an endoscopy", routine: "Know the warning signs" } },
  liver: { elevated: "Regular monitoring with ultrasound and blood tests can catch changes early.", routine: "Healthy weight and up-to-date hepatitis vaccines are your best steps.", action: { elevated: "Ask about liver monitoring", routine: "Review your hepatitis status" } },
  thyroid: { elevated: "A simple neck exam and ultrasound can catch most issues early.", routine: "Routine physical exams are usually sufficient.", action: { elevated: "Request a thyroid check", routine: "Stay aware during checkups" } },
  skin: { elevated: "Annual full-body skin exams are your best defense.", routine: "Wear sunscreen and do monthly self-checks.", action: { elevated: "Book a skin exam", routine: "Start monthly self-checks" } },
  prostate: { elevated: "A simple PSA blood test is the first step.", routine: "Talk to your doctor about when to start.", action: { elevated: "Ask about a PSA test", routine: "Plan your PSA timeline" } },
  ovarian: { elevated: "CA-125 blood tests and ultrasound can help.", routine: "Report persistent symptoms to your doctor.", action: { elevated: "Discuss monitoring options", routine: "Know the symptoms" } },
  uterine: { elevated: "Report any unexpected vaginal bleeding promptly.", routine: "Keep up with regular gynecological exams.", action: { elevated: "Talk to your gynecologist", routine: "Stay up to date on exams" } },
  testicular: { elevated: "One of the most treatable cancers when caught early.", routine: "Self-exams are simple and effective.", action: { elevated: "Learn the self-exam technique", routine: "Start monthly self-exams" } },
};
const DEFAULT_COPY = { elevated: "Discuss screening with your doctor.", routine: "Stay on top of routine screenings.", action: { elevated: "Talk to your doctor", routine: "Plan your screening" } };

// ─── Helpers ───
function mapRecommendationsToCards(recommendations, surveyData) {
  const { stronglyRecommend = [], recommend = [] } = recommendations || {};
  const allItems = [...stronglyRecommend, ...recommend];
  const iconMap = { gastric: Activity, liver: Activity, lung: ShieldCheck, breast: Heart, cervical: ShieldCheck, colorectal: Activity, thyroid: ShieldCheck, skin: ShieldCheck, prostate: ShieldCheck, ovarian: Heart, uterine: Heart, testicular: ShieldCheck };
  return allItems.map((item) => {
    const score = item.riskScore || 1.0;
    const isElevated = score > 1.2;
    let statusColor = 'green', priority = 'On Your Radar';
    if (score >= 2.5) { statusColor = 'red'; priority = 'Act Now'; }
    else if (score >= 1.5) { statusColor = 'orange'; priority = 'Worth a Conversation'; }
    else if (score > 1.2) { statusColor = 'stone'; priority = 'Good to Know'; }
    const riskFactors = item.riskFactors || [];
    const screening = item.screening || {};
    const copy = CANCER_COPY[item.cancerKey] || DEFAULT_COPY;
    const sources = [];
    if (item.guidelineSource?.org) sources.push(`${item.guidelineSource.org}${item.guidelineSource.note ? ': ' + item.guidelineSource.note : ''}`);
    if (screening.source) sources.push(screening.source);
    const clinicalParts = [];
    if (riskFactors.length > 0) { clinicalParts.push("Profile findings:"); riskFactors.forEach(r => clinicalParts.push(`• ${r.factor || r}${r.detail ? ' — ' + r.detail : ''}`)); }
    if (screening.method) { clinicalParts.push(''); clinicalParts.push(`Screening: ${screening.method}`); if (screening.interval) clinicalParts.push(`Interval: ${screening.interval}`); if (screening.who) clinicalParts.push(`Who: ${screening.who}`); }
    if (screening.highRisk) { clinicalParts.push(''); clinicalParts.push(`High-risk: ${screening.highRisk}`); }
    if (item.statistics?.length > 0) { clinicalParts.push(''); item.statistics.forEach(s => clinicalParts.push(`• ${s.label}: ${s.value}`)); }
    return { title: item.cancer || 'Screening', priority, multiplier: score.toFixed(1), statusColor, icon: iconMap[item.cancerKey] || ShieldCheck, summary: isElevated ? copy.elevated : copy.routine, action: isElevated ? copy.action.elevated : copy.action.routine, clinicalText: clinicalParts.join('\n'), sources, isGovRecommended: item.tier === 'primary' };
  });
}

function getArchetype(surveyData) {
  if (!surveyData) return { name: "The Seedling", emoji: "🌱", superpower: "Taking the first step", watchOut: "The unknown", percentile: "Top 60%", tagline: "Everything ahead of you" };
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits, familyCancerHistory = [], livedWithSmoker } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  const isHealthyBmi = bmi && bmi >= 18.5 && bmi < 25;
  const isNonSmoker = (!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const isLowAlcohol = !alcoholConsumption || alcoholConsumption === "none" || alcoholConsumption === "under_7";
  const hasGoodDiet = !dietHabits || dietHabits === 'no' || dietHabits === 'unsure';
  const noSecondhandSmoke = livedWithSmoker !== 'yes';
  const healthScore = [isHealthyBmi, isNonSmoker, isLowAlcohol, hasGoodDiet, noSecondhandSmoke].filter(Boolean).length;
  const isFormerSmoker = (smokingHistory > 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  const filteredFamily = (Array.isArray(familyCancerHistory) ? familyCancerHistory : []).filter(fh => fh.toLowerCase() !== 'none');
  const hasFamilyHistory = filteredFamily.length > 0;
  const alcoholIsWeakSpot = !isLowAlcohol;
  const dietIsWeakSpot = !hasGoodDiet;
  const weakSpot = !isNonSmoker ? "Smoking exposure" : !isLowAlcohol ? "Alcohol intake" : !hasGoodDiet ? "Processed food habits" : !isHealthyBmi ? "Weight management" : !noSecondhandSmoke ? "Secondhand smoke exposure" : hasFamilyHistory ? "Family history" : "Complacency";
  if (isFormerSmoker && healthScore >= 3) return { name: "The Phoenix", emoji: "🔥", superpower: "You made the hardest change already", watchOut: weakSpot, percentile: "Top 15%", tagline: "Rose from the ashes" };
  if (healthScore === 5 && !hasFamilyHistory) return { name: "The Zen Master", emoji: "🧘", superpower: "Balance in all things", watchOut: weakSpot, percentile: "Top 8%", tagline: "Calm, in control" };
  if (healthScore === 5 && hasFamilyHistory) return { name: "The Sentinel", emoji: "🛡️", superpower: "Proactive despite the odds", watchOut: weakSpot, percentile: "Top 10%", tagline: "Guarding what you can't control" };
  if (healthScore === 4) return { name: "The Adventurer", emoji: "🧭", superpower: "Mostly nailing it", watchOut: weakSpot, percentile: "Top 20%", tagline: "Mostly solid, one indulgence" };
  if (healthScore >= 2) {
    if (alcoholIsWeakSpot || dietIsWeakSpot) return { name: "The Rebel", emoji: "🎸", superpower: "You own the risks", watchOut: weakSpot, percentile: "Top 35%", tagline: "Lives a little, knows the score" };
    return { name: "The Night Owl", emoji: "🦉", superpower: "Self-aware and honest", watchOut: weakSpot, percentile: "Top 40%", tagline: "Eyes wide open" };
  }
  return { name: "The Seedling", emoji: "🌱", superpower: "The first step is the hardest", watchOut: weakSpot, percentile: "Top 60%", tagline: "Everything ahead of you" };
}

function getWins(surveyData) {
  if (!surveyData) return [];
  const wins = [];
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  if (bmi && bmi >= 18.5 && bmi < 25) wins.push({ title: `BMI ${bmi.toFixed(1)}`, desc: "Healthy range" });
  if ((!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0)) wins.push({ title: "Smoke-Free", desc: "#1 preventable cause avoided" });
  if (dietHabits === 'no') wins.push({ title: "Clean Diet", desc: "Low processed food" });
  if (!alcoholConsumption || alcoholConsumption === "none") wins.push({ title: "Low Alcohol", desc: "Minimal intake" });
  if (wins.length === 0) wins.push({ title: "Taking Action", desc: "You showed up" });
  return wins.slice(0, 3);
}

// ─── Lifestyle factor calculator for progress bars ───
function getLifestyleFactors(surveyData) {
  if (!surveyData) return [];
  const { weightInKg, heightInCm, smokingHistory, cigarettesPerDay, alcoholConsumption, dietHabits, livedWithSmoker } = surveyData;
  const bmi = (weightInKg && heightInCm) ? weightInKg / ((heightInCm / 100) ** 2) : null;
  
  const factors = [];
  
  // BMI: 18.5-25 is good. Map 15-35 range to 0-100, with 18.5-25 being "good" zone
  if (bmi) {
    const isGood = bmi >= 18.5 && bmi < 25;
    // Score: 100 = perfect (BMI 21.75), decreasing as you move away
    const ideal = 21.75;
    const score = Math.max(0, Math.min(100, 100 - Math.abs(bmi - ideal) * 10));
    factors.push({ label: 'BMI', value: bmi.toFixed(1), score, isGood, goodLabel: 'Healthy', badLabel: 'Needs Attention', color: isGood ? '#10B981' : '#F59E0B' });
  }
  
  // Smoking
  const isNonSmoker = (!smokingHistory || smokingHistory === 0) && (!cigarettesPerDay || cigarettesPerDay === 0);
  factors.push({ label: 'Smoking', value: isNonSmoker ? 'Non-smoker' : `${smokingHistory || 0}yr history`, score: isNonSmoker ? 95 : Math.max(5, 50 - (smokingHistory || 0) * 3), isGood: isNonSmoker, goodLabel: 'Smoke-Free', badLabel: 'Risk Factor', color: isNonSmoker ? '#10B981' : '#EF4444' });
  
  // Alcohol
  const alcoholScores = { 'none': 95, 'under_7': 75, '7_to_14': 40, 'over_14': 15 };
  const alcScore = alcoholScores[alcoholConsumption] || 95;
  const isLowAlc = !alcoholConsumption || alcoholConsumption === "none" || alcoholConsumption === "under_7";
  factors.push({ label: 'Alcohol', value: alcoholConsumption === 'none' || !alcoholConsumption ? 'None' : alcoholConsumption === 'under_7' ? '<7/week' : alcoholConsumption === '7_to_14' ? '7-14/week' : '14+/week', score: alcScore, isGood: isLowAlc, goodLabel: 'Low Risk', badLabel: 'Elevated', color: isLowAlc ? '#10B981' : '#F59E0B' });
  
  // Diet
  const isGoodDiet = !dietHabits || dietHabits === 'no' || dietHabits === 'unsure';
  factors.push({ label: 'Diet', value: isGoodDiet ? 'Whole foods' : 'Processed foods', score: isGoodDiet ? 90 : 35, isGood: isGoodDiet, goodLabel: 'Clean', badLabel: 'Needs Work', color: isGoodDiet ? '#10B981' : '#F59E0B' });
  
  // Secondhand smoke
  const noSHS = livedWithSmoker !== 'yes';
  factors.push({ label: 'Secondhand Smoke', value: noSHS ? 'No exposure' : 'Exposed', score: noSHS ? 95 : 30, isGood: noSHS, goodLabel: 'Clear', badLabel: 'Exposed', color: noSHS ? '#10B981' : '#EF4444' });
  
  return factors;
}

async function generatePDF(surveyData, recommendations) {
  const jsPDF = await loadJsPDF();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth(); const pageH = doc.internal.pageSize.getHeight(); const margin = 20; let y = margin;
  const checkPage = (n = 20) => { if (y + n > pageH - 20) { doc.addPage(); y = margin; } };
  doc.setFillColor(68, 98, 74); doc.rect(0, 0, pageW, 40, 'F');
  doc.setTextColor(255); doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.text('Pebble Health', margin, 18);
  doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.text('Cancer Screening Report', margin, 27);
  y = 50;
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};
  const contentW = pageW - margin * 2;
  const renderSection = (title, items, color) => { if (!items.length) return; checkPage(30); doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(...color); doc.text(title, margin, y); y += 10; items.forEach(item => { checkPage(20); doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(30); doc.text(item.cancer || 'Screening', margin + 4, y); y += 8; if (item.description) { doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(80); const lines = doc.splitTextToSize(item.description, contentW - 8); doc.text(lines, margin + 4, y); y += lines.length * 4 + 4; } y += 6; }); };
  renderSection('Strongly Recommended', stronglyRecommend, [185, 28, 28]);
  renderSection('Recommended', recommend, [180, 120, 20]);
  renderSection('Routine', belowAverage, [80, 120, 80]);
  doc.save('pebble-health-screening-report.pdf');
}

// ─── Share Modal ───
const ShareModal = ({ isOpen, onClose, imageUrl }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><Sparkles size={18} /> Share Your Result</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-6 flex flex-col items-center gap-4">
          {imageUrl ? <img src={imageUrl} alt="Archetype" className="w-64 rounded-2xl shadow-lg" /> : <div className="w-64 aspect-[4/5] bg-gray-200 animate-pulse rounded-2xl" />}
          <button onClick={() => { const a = document.createElement('a'); a.download = 'pebblehealth-archetype.png'; a.href = imageUrl; a.click(); }} className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold flex items-center justify-center gap-2"><ImageIcon size={18} /> Save Image</button>
          <button onClick={() => { window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("My health archetype from Pebble Health ✨")}&url=${encodeURIComponent("https://pebblehealth.ai")}`, '_blank'); }} className="w-full py-3 bg-black text-white rounded-2xl font-bold flex items-center justify-center gap-2"><Twitter size={18} /> Share to X</button>
        </div>
      </div>
    </div>
  );
};

// ─── Gradient map for archetypes ───
const ARCHETYPE_GRADIENTS = {
  "The Phoenix": "from-orange-400 via-red-500 to-yellow-500",
  "The Zen Master": "from-emerald-400 via-teal-500 to-cyan-500",
  "The Sentinel": "from-blue-400 via-indigo-500 to-purple-500",
  "The Adventurer": "from-amber-400 via-orange-500 to-rose-500",
  "The Rebel": "from-pink-400 via-rose-500 to-red-500",
  "The Night Owl": "from-indigo-400 via-purple-500 to-violet-600",
  "The Seedling": "from-lime-400 via-emerald-500 to-teal-500",
};

// ═══════════════════════════════════════════════════════════════
//  ResultPageV5 — "16personalities / Character-First"
// ═══════════════════════════════════════════════════════════════
export default function ResultPageV5() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [expandedPriority, setExpandedPriority] = useState(null);

  const analysisResults = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("omnicare_analysis_results")); } catch { return null; }
  }, []);
  const { surveyData, recommendations } = analysisResults || {};
  const prioritiesData = useMemo(() => {
    const cards = mapRecommendationsToCards(recommendations, surveyData);
    return cards.sort((a, b) => { if (a.isGovRecommended && !b.isGovRecommended) return -1; if (b.isGovRecommended && !a.isGovRecommended) return 1; return parseFloat(b.multiplier) - parseFloat(a.multiplier); });
  }, [recommendations, surveyData]);
  const archetype = useMemo(() => getArchetype(surveyData), [surveyData]);
  const wins = useMemo(() => getWins(surveyData), [surveyData]);
  const lifestyleFactors = useMemo(() => getLifestyleFactors(surveyData), [surveyData]);

  const handleCaptureCard = async () => {
    if (!cardRef.current) return;
    setIsCapturing(true);
    try {
      const html2canvas = await loadHtml2Canvas();
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, logging: false });
      setCapturedImage(canvas.toDataURL('image/png'));
      setShareModalOpen(true);
    } catch (e) { console.error(e); }
    finally { setIsCapturing(false); }
  };

  if (!analysisResults) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">No results found</h2>
          <button onClick={() => navigate('/survey/1')} className="px-6 py-3 bg-purple-600 text-white rounded-2xl font-bold">Take the Quiz</button>
        </div>
      </div>
    );
  }

  const gradient = ARCHETYPE_GRADIENTS[archetype.name] || ARCHETYPE_GRADIENTS["The Seedling"];
  const dotColor = { red: 'bg-red-500', orange: 'bg-amber-500', stone: 'bg-gray-400', green: 'bg-emerald-500' };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Floating header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/landing')}>
            <img src={omnicareLogo} alt="Pebble Health" className="w-6 h-6 opacity-90" />
            <span className="text-sm font-semibold tracking-tight">Pebble Health</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/results')} className="hidden sm:block text-xs text-gray-400 hover:text-gray-600">Classic View</button>
            <button onClick={handleCaptureCard} className="flex items-center gap-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full transition-colors">
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </header>

      {/* ═══ SECTION 1: Full-viewport archetype hero ═══ */}
      <section ref={cardRef} className={`min-h-screen flex flex-col items-center justify-center relative bg-gradient-to-br ${gradient} text-white overflow-hidden`}>
        {/* Decorative circles */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 text-center px-6 max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-white/60 mb-6">Your Health Archetype</p>
          <div className="text-8xl sm:text-9xl mb-6">{archetype.emoji}</div>
          <h1 className="text-5xl sm:text-7xl font-extrabold tracking-tight mb-4">{archetype.name}</h1>
          <p className="text-xl sm:text-2xl text-white/70 font-light mb-8">{archetype.tagline}</p>
          
          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            <div className="bg-white/15 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/20">
              <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">Your Strength</p>
              <p className="font-semibold text-sm mt-0.5">{archetype.superpower}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/20">
              <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">Watch Out For</p>
              <p className="font-semibold text-sm mt-0.5">{archetype.watchOut}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/20">
              <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">Ranking</p>
              <p className="font-semibold text-sm mt-0.5">{archetype.percentile} of users</p>
            </div>
          </div>

          {/* Scroll hint */}
          <div className="animate-bounce">
            <ChevronDown size={28} className="text-white/40 mx-auto" />
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2: Lifestyle Factors — compact 2-col grid with inline bars ═══ */}
      <section className="max-w-3xl mx-auto px-6 py-14">
        <div className="text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-1">Your Lifestyle Profile</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold">Here's who you are</h2>
        </div>

        {/* 2-col grid of compact factor bars on desktop, 1-col mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {lifestyleFactors.map((factor, i) => (
            <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
              <div className="w-[72px] flex-shrink-0">
                <span className="text-xs font-bold text-gray-800">{factor.label}</span>
              </div>
              <div className="relative flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-emerald-100/50 rounded-r-full" />
                <div className="absolute top-0 bottom-0 left-0 rounded-full transition-all duration-700" style={{ width: `${factor.score}%`, background: factor.isGood ? 'linear-gradient(90deg, #34D399, #10B981)' : factor.score < 30 ? 'linear-gradient(90deg, #F87171, #EF4444)' : 'linear-gradient(90deg, #FBBF24, #F59E0B)' }} />
                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-300" />
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${factor.isGood ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {factor.value}
              </span>
            </div>
          ))}
        </div>

        {/* Wins — inline chips */}
        {wins.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center gap-2 justify-center">
            <span className="text-xs font-bold text-emerald-700 flex items-center gap-1"><CheckCircle2 size={14} /> Working for you:</span>
            {wins.map((w, i) => (
              <span key={i} className="text-xs bg-emerald-50 text-emerald-800 px-3 py-1 rounded-full border border-emerald-200 font-medium">{w.title}</span>
            ))}
          </div>
        )}
      </section>

      {/* ═══ SECTION 3: Screening Priorities — tight accordion ═══ */}
      <section className="bg-gray-50 py-14">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-1">Screening Priorities</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold">Here's what matters</h2>
          </div>

          {prioritiesData.length > 0 ? (
            <div className="space-y-1.5">
              {prioritiesData.map((data, index) => {
                const isOpen = expandedPriority === index;
                return (
                  <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-sm">
                    <button
                      onClick={() => setExpandedPriority(isOpen ? null : index)}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left"
                    >
                      <span className="text-lg font-extrabold text-gray-200 w-6 flex-shrink-0 text-right">{index + 1}</span>
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor[data.statusColor]}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-gray-900 truncate">{data.title}</h3>
                          <span className="text-[10px] font-mono font-bold text-gray-400">{data.multiplier}×</span>
                          {data.isGovRecommended && <span className="text-[8px] font-bold text-blue-600 bg-blue-50 px-1 py-px rounded">GOV</span>}
                        </div>
                      </div>
                      <ChevronDown size={16} className={`text-gray-300 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-3 border-t border-gray-100">
                        <p className="text-xs text-gray-600 mt-2 leading-relaxed">{data.summary}</p>
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full mt-2">{data.action} <ArrowRight size={11} /></span>
                        {data.clinicalText && (
                          <details className="mt-2">
                            <summary className="text-[10px] font-semibold text-gray-400 cursor-pointer flex items-center gap-1"><BookOpen size={11} /> Science & sources</summary>
                            <div className="mt-1.5 p-2.5 bg-gray-50 rounded-lg text-[10px] text-gray-500 whitespace-pre-line border border-gray-100">
                              {data.clinicalText}
                              {data.sources?.filter(Boolean).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  {data.sources.filter(Boolean).map((s, i) => <p key={i} className="text-[9px] text-gray-400">[{i + 1}] {s}</p>)}
                                </div>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
              <ShieldCheck className="text-emerald-500 mx-auto mb-3" size={36} />
              <h3 className="text-lg font-bold mb-1">All Clear!</h3>
              <p className="text-gray-500 text-sm">No elevated risk factors detected.</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══ SECTION 4: CTA — compact ═══ */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`rounded-2xl p-8 sm:p-10 text-center text-white bg-gradient-to-br ${gradient} relative overflow-hidden`}>
            <div className="absolute top-0 right-0 opacity-10 translate-x-1/4 -translate-y-1/4"><FileText size={200} /></div>
            <div className="relative z-10 space-y-4">
              <h3 className="text-xl sm:text-2xl font-bold">Get your full clinical report</h3>
              <p className="text-white/60 text-sm max-w-md mx-auto">Download a PDF to bring to your doctor's appointment.</p>
              <button onClick={() => generatePDF(surveyData, recommendations)} className="px-6 py-3 bg-white text-gray-900 rounded-xl font-bold text-sm flex items-center justify-center gap-2 mx-auto hover:bg-gray-100 transition-all">
                <Download size={16} /> Download PDF
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="sm:hidden text-center pb-8">
        <button onClick={() => navigate('/results')} className="text-gray-400 underline text-sm">Classic View →</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} imageUrl={capturedImage} />
    </div>
  );
}
